import { CommonModule } from '@angular/common';
import { ETomKpi, KpiComissoesComponent, SEM_DADO_KPI } from '@/features/comissoes/dashboard-comissoes/components/kpi-comissoes/kpi-comissoes';
import { ChangeDetectionStrategy, Component, DestroyRef, OnChanges, OnInit, SimpleChanges, AfterViewInit, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { ComissaoReuniaoService } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.service';
import { Popover } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { TablePageEvent } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AtividadeComissao } from '@/features/comissoes/dashboard-comissoes/atividades-comissoes.model';
import { ComissaoEstatisticaAgrupadaResponse } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { CardContent } from '@/shared/components/card-content/card-content';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';

export type AcaoTabelaAtividadesComissoes = 'editar' | 'excluir' | 'ir-para-reuniao' | 'visualizar-comissao-nucleo' | 'visualizar-selecionada';

export interface AtividadeRowItem {
    codigo: number;
    status: { value: string; severity?: 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' | 'info'; color?: string };
    numeroAtividade: string;
    codigoReuniao: string;
    protocolo: string;
    dataInicio: string;
    dataFim: string;
    comissaoNucleo: string;
    responsavel: string;
    descricao: string;
    source: AtividadeComissao;
}

@Component({
    selector: 'app-tabela-atividades-comissoes',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, CustomTableComponent, TagModule, BotaoAcoes, CardContainer, Popover, ButtonModule, TooltipModule, FormsModule, CardContent, KpiComissoesComponent],
    templateUrl: './tabela-atividades-comissoes.html',
    styleUrls: ['./tabela-atividades-comissoes.scss']
})
/**
 * Tabela de atividades da comissão (custom-table + visão card): seleção de linha, ações da
 * atividade dentro do menu "Opções", indicadores (total/pendentes/resolvidas) e colunas configuráveis.
 */
export class TabelaAtividadesComissoesComponent implements OnChanges, OnInit, AfterViewInit {
    protected readonly EInconPos = EInconPos;
    protected readonly linhasPorPaginaPadrao = 20;
    protected readonly opcoesLinhasPorPagina = [10, 20, 50, 100];

    private readonly destroyRef = inject(DestroyRef);
    /**
     * A foto vem do endpoint de assistente de reunião — apesar do nome, ele resolve QUALQUER
     * solicitante: tenta a foto do usuário na UF, depois em qualquer UF, e por fim a imagem de
     * identificação do médico. É o mesmo caminho que a tabela de Acompanhamento usa.
     */
    private readonly comissaoReuniaoService = inject(ComissaoReuniaoService);

    /**
     * Cache das fotos por responsável, chaveado por id+UF. `null` significa "já buscou e não há
     * foto" — evita rebuscar a cada troca de página, e o mesmo médico responde por várias
     * atividades com a mesma foto. Na base de hoje só um em seis tem retrato, então o caminho sem
     * foto é o comum: cai nas iniciais.
     */
    protected readonly fotosResponsaveis = signal<ReadonlyMap<string, string | null>>(new Map());

    opcoesColunas: { label: string; value: string }[] = [];
    colunasSelecionadas: string[] = [];

    ngOnInit(): void {
        this.inicializarColunas();
    }

    inicializarColunas(): void {
        this.opcoesColunas = Object.entries(this.metadataTabela.headers).map(([key, header]) => ({
            label: header.value,
            value: key
        }));
        this.colunasSelecionadas = Object.entries(this.metadataTabela.headers)
            .filter(([_, header]) => !header.hidden)
            .map(([key, _]) => key);
    }

    atualizarColunasVisiveis(): void {
        const novosHeaders = { ...this.metadataTabela.headers };
        Object.keys(novosHeaders).forEach((key) => {
            novosHeaders[key] = {
                ...novosHeaders[key],
                hidden: !this.colunasSelecionadas.includes(key)
            };
        });
        this.metadataTabela = {
            ...this.metadataTabela,
            headers: novosHeaders
        };
    }

    todasSelecionadas(): boolean {
        return this.colunasSelecionadas.length === this.opcoesColunas.length;
    }

    toggleTodasColunas(): void {
        if (this.todasSelecionadas()) {
            this.colunasSelecionadas = [];
        } else {
            this.colunasSelecionadas = this.opcoesColunas.map((o) => o.value);
        }
        this.atualizarColunasVisiveis();
    }

    toggleColuna(colKey: string): void {
        if (this.colunasSelecionadas.includes(colKey)) {
            this.colunasSelecionadas = this.colunasSelecionadas.filter((c) => c !== colKey);
        } else {
            this.colunasSelecionadas = [...this.colunasSelecionadas, colKey];
        }
        this.atualizarColunasVisiveis();
    }

    /** Atividades da página atual. */
    readonly atividades = input<AtividadeComissao[]>([]);
    /** Total server-side (para rótulo/indicador). */
    readonly totalRegistros = input<number | null>(null);
    /** Índice do primeiro registro (paginação). */
    readonly first = input(0);
    /** Linhas por página. */
    readonly rows = input(this.linhasPorPaginaPadrao);
    /** Atividade selecionada (destaca a linha). */
    readonly atividadeSelecionada = input<AtividadeComissao | null>(null);
    /** Exibe estado de carregamento. */
    readonly carregando = input(false);
    /** Itens do menu "Opções" (ações gerais) exibido no header do card. */
    readonly itensOpcoes = input<MenuItem[]>([]);
    /** Estado do painel de filtros do dashboard (controla o rótulo/ação do botão). */
    readonly filtrosVisiveis = input(true);
    /**
     * Contagem por situação do conjunto inteiro, vinda de `/comissoes/estatisticas/atividades/situacao`
     * com o MESMO filtro da listagem. Antes daqui os indicadores contavam só a página carregada: ao
     * lado de um total server-side de 604, "Pendentes 10 / Resolvidas 10" na página 1 virava
     * "3 / 17" na página 2. Nulo enquanto a consulta não responde — os indicadores mostram "–" em
     * vez de um número da página, que seria errado.
     */
    readonly situacoesResumo = input<ComissaoEstatisticaAgrupadaResponse[] | null>(null);

    /** Emite a atividade selecionada (ou null). */
    readonly selecionarAtividade = output<AtividadeComissao | null>();
    /** Emite a ação escolhida no menu "Opções". */
    readonly acaoTabela = output<AcaoTabelaAtividadesComissoes>();
    /** Emite a troca de página. */
    readonly paginaAlterada = output<TablePageEvent>();
    /** Alterna o painel de filtros do dashboard. */
    readonly alternarFiltros = output<void>();

    /** Tom de cada indicador, exposto para o template não carregar string solta. */
    protected readonly ETomKpi = ETomKpi;

    /** Valor do cartão já como texto: `null` (resumo ainda não chegou) vira o traço de ausência. */
    protected valorIndicador(total?: number | null): string {
        return total === null || total === undefined ? SEM_DADO_KPI : String(total);
    }

    /**
     * Legenda dos cartões de situação: a fatia que aquela situação ocupa no total. Os três
     * cartões saem do mesmo agregado server-side, então a divisão compara o mesmo escopo — se
     * um dia as fontes divergirem, esta conta passa a mentir e precisa sair.
     */
    protected legendaFatia(parte?: number | null): string {
        const total = this.totalAtividades;
        if (parte === null || parte === undefined || total <= 0) {
            return '';
        }
        return `${Math.round((parte / total) * 100)}% do total`;
    }

    /** Total de atividades (server-side quando disponível). */
    get totalAtividades(): number {
        return this.totalRegistros() ?? (this.atividades() || []).length;
    }

    /** Atividades pendentes no conjunto inteiro (`null` enquanto o resumo não chega). */
    get totalPendentes(): number | null {
        return this.totalDaSituacao('pendente');
    }

    /** Atividades resolvidas no conjunto inteiro (`null` enquanto o resumo não chega). */
    get totalResolvidas(): number | null {
        return this.totalDaSituacao('resolvido');
    }

    /**
     * Casa a linha do resumo pelo mesmo rótulo que a coluna Situação exibe ("Pendente"/"Resolvido"),
     * comparado sem acento e sem caixa: assim indicador e célula falam do mesmo vocabulário e um não
     * pode divergir do outro em silêncio. Situação ausente no resumo é zero de verdade — o servidor
     * só omite o grupo quando ele não tem registro.
     */
    private totalDaSituacao(status: AtividadeComissao['status']): number | null {
        const resumo = this.situacoesResumo();
        if (!resumo) return null;

        const alvo = this.normalizarTexto(this.statusRotulo(status));
        const item = resumo.find((linha) => this.normalizarTexto(linha.descricao ?? '') === alvo);
        return item?.total ?? 0;
    }

    private normalizarTexto(valor: string): string {
        return valor
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();
    }

    /** Referência à custom-table para sincronizar a linha selecionada. */
    readonly customTable = viewChild(CustomTableComponent);

    /**
     * Cinco colunas compostas, cada uma empilhando as linhas do próprio assunto — o mesmo desenho da
     * tabela de Acompanhamento. Dez colunas de um campo cada, todas em `nowrap`, empurravam a tabela
     * para 2370px: ler uma única atividade exigia rolar de ponta a ponta. Agrupadas por assunto a
     * linha inteira cabe na tela, e nenhum campo se perde — os dez continuam visíveis, só que
     * empilhados dentro da coluna do seu assunto.
     *
     * O responsável tem coluna própria porque carrega retrato: como linha secundária da comissão o
     * círculo competia com o nome dela, que é o dado principal daquela célula. E a situação deixou de
     * ser coluna — virou pictograma dentro da Identificação, no mesmo princípio de Acompanhamento:
     * numa coluna de rótulos que se repetem linha após linha, a forma se lê de relance e a palavra
     * não. O nome por extenso segue no tooltip e no `aria-label`. A troca liberou os 110px da antiga
     * coluna Situação para os 190px que o retrato pede.
     *
     * A chave de cada coluna é o campo principal que ela mostra, para exportação e ordenação
     * continuarem apontando para um dado real. O preço do agrupamento é o seletor de colunas passar a
     * esconder assuntos, não campos isolados.
     */
    metadataTabela: TableMetadata = {
        headers: {
            codigo: { type: 'text', value: 'Identificação', hidden: false, default: '-' },
            numeroAtividade: { type: 'text', value: 'Atividade', hidden: false, default: '-' },
            dataInicio: { type: 'text', value: 'Prazo', hidden: false, default: '-' },
            comissaoNucleo: { type: 'text', value: 'Comissão / Núcleo', hidden: false, default: '-' },
            responsavel: { type: 'text', value: 'Responsável', hidden: false, default: '-' }
        },
        title: 'comissaoNucleo',
        tag: 'status'
    };

    /** O traço de "sem valor" que o mapeamento usa — evita comparar com `'-'` solto no template. */
    private readonly semValor = '-';

    /** Protocolo só ganha o selo quando existe; sem ele a linha inteira some da célula. */
    exibeProtocolo(rowData: AtividadeRowItem): boolean {
        return !!rowData.protocolo && rowData.protocolo !== this.semValor;
    }

    /** Nem toda atividade nasce de uma reunião: sem código, a linha não aparece. */
    exibeReuniao(rowData: AtividadeRowItem): boolean {
        return !!rowData.codigoReuniao && rowData.codigoReuniao !== this.semValor;
    }

    /** Atividade em aberto não tem data fim — a segunda linha do prazo fica de fora. */
    exibePrazoFim(rowData: AtividadeRowItem): boolean {
        return !!rowData.dataFim && rowData.dataFim !== this.semValor;
    }

    /**
     * Atividade que abre e fecha no mesmo dia repetiria a data ("02/07/2026 / até 02/07/2026"). A
     * segunda linha então diz o que a repetição significaria, em vez de repetir — e o prazo continua
     * com duas linhas em toda a tabela, que é o que mantém as colunas niveladas.
     */
    prazoNoMesmoDia(rowData: AtividadeRowItem): boolean {
        return this.exibePrazoFim(rowData) && rowData.dataFim === rowData.dataInicio;
    }

    /** Sem responsável a ausência é dita por extenso, em meia-tinta, como em Acompanhamento. */
    exibeResponsavel(rowData: AtividadeRowItem): boolean {
        return !!rowData.responsavel && rowData.responsavel !== this.semValor;
    }

    /** Descrição vazia não deve render "-" numa linha própria. */
    exibeDescricao(rowData: AtividadeRowItem): boolean {
        return !!rowData.descricao && rowData.descricao !== this.semValor;
    }

    dataMapped: AtividadeRowItem[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['atividades']) {
            this.atualizarDadosMapeados();
        }
        if (changes['atividadeSelecionada']) {
            this.sincronizarSelecao();
        }
    }

    ngAfterViewInit(): void {
        this.sincronizarSelecao();
    }

    private atualizarDadosMapeados(): void {
        this.dataMapped = (this.atividades() || []).map((atividade) => ({
            codigo: Number(atividade.codigo),
            status: {
                value: this.statusRotulo(atividade.status),
                severity: this.statusSeveridade(atividade.status)
            },
            numeroAtividade: atividade.numeroAtividade,
            codigoReuniao: atividade.codigoReuniao,
            protocolo: atividade.numeroProtocolo || (atividade.idProtocolo ? String(atividade.idProtocolo) : '-'),
            dataInicio: this.formatarDataSemHora(atividade.dataInicio),
            dataFim: this.formatarDataSemHora(atividade.dataFim),
            comissaoNucleo: atividade.comissaoNucleo,
            responsavel: atividade.responsavel,
            descricao: atividade.descricao,
            source: atividade
        }));
        this.sincronizarSelecao();
        this.carregarFotosResponsaveis();
    }

    /** Busca as fotos dos responsáveis da página que ainda não estão no cache. */
    private carregarFotosResponsaveis(): void {
        const pendentes = new Map<string, { id: number; uf?: string }>();
        for (const atividade of this.atividades() || []) {
            const id = atividade.idSolicitanteResponsavel;
            if (!id) continue;
            const chave = this.chaveFotoResponsavel(id, atividade.ufSolicitanteResponsavel);
            if (!this.fotosResponsaveis().has(chave) && !pendentes.has(chave)) {
                pendentes.set(chave, { id, uf: atividade.ufSolicitanteResponsavel ?? undefined });
            }
        }

        pendentes.forEach((alvo, chave) => {
            this.comissaoReuniaoService
                .obterFotoAssistente(alvo.id, alvo.uf)
                .pipe(
                    // Foto é enfeite: falha vira "sem foto" (iniciais), nunca erro na tela.
                    catchError(() => of({ foto: null })),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((resposta) => {
                    const mapa = new Map(this.fotosResponsaveis());
                    mapa.set(chave, resposta.foto ?? null);
                    this.fotosResponsaveis.set(mapa);
                });
        });
    }

    private chaveFotoResponsavel(id: number, uf?: string | null): string {
        return `${id}|${uf ?? ''}`;
    }

    /** Foto do responsável da atividade, ou `null` enquanto não chega / quando não existe. */
    fotoResponsavel(rowData: AtividadeRowItem): string | null {
        const id = rowData.source.idSolicitanteResponsavel;
        if (!id) return null;
        return this.fotosResponsaveis().get(this.chaveFotoResponsavel(id, rowData.source.ufSolicitanteResponsavel)) ?? null;
    }

    /**
     * Iniciais do responsável, para o círculo que substitui a foto ausente. Sem elas a coluna
     * ficaria desalinhada entre linhas com e sem retrato — e sem retrato é a maioria.
     */
    iniciaisResponsavel(rowData: AtividadeRowItem): string {
        const limpo = (rowData.responsavel || '').trim();
        if (!limpo || limpo === this.semValor) return '';
        return limpo
            .split(/\s+/)
            .slice(0, 2)
            .map((parte) => parte[0])
            .join('')
            .toUpperCase();
    }

    private sincronizarSelecao(): void {
        const tabela = this.customTable();
        if (!tabela) return;

        if (!this.atividadeSelecionada()) {
            tabela.selectedRow.set(null);
            return;
        }

        const itemMapeado = this.dataMapped.find((item) => item.codigo === Number(this.atividadeSelecionada()?.codigo));
        tabela.selectedRow.set(itemMapeado || null);
    }

    private formatarDataSemHora(valor?: Date | string | null): string {
        if (!valor) return '-';
        try {
            const d = typeof valor === 'string' ? new Date(valor) : valor;
            if (isNaN(d.getTime())) return '-';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return String(valor);
        }
    }

    isRowSelected(rowData: AtividadeRowItem): boolean {
        return Number(this.atividadeSelecionada()?.codigo) === rowData.codigo;
    }

    handleRowClick(rowItem: AtividadeRowItem | null): void {
        this.onSelecaoAlterada(rowItem ? rowItem.source : null);
    }

    onSelecaoAlterada(valor: AtividadeComissao | null): void {
        this.selecionarAtividade.emit(valor);
    }

    onVisualizar(atividade: AtividadeComissao): void {
        this.selecionarAtividade.emit(atividade);
        this.emitirAcao('visualizar-selecionada');
    }

    onPaginaAlterada(event: TablePageEvent): void {
        this.paginaAlterada.emit(event);
    }

    itensAcoesTabela(): AppMenuItem[] {
        const possuiAtividade = Boolean(this.atividadeSelecionada());
        return [
            {
                label: 'Editar',
                icon: 'pi pi-pencil',
                styleClass: 'bg-blue-500',
                disabled: !possuiAtividade,
                permission: PERMISSOES.comissoes.atividades.editar,
                command: () => this.emitirAcao('editar')
            },
            {
                label: 'Excluir',
                icon: 'pi pi-trash',
                styleClass: 'bg-red-500',
                disabled: !possuiAtividade,
                permission: PERMISSOES.comissoes.atividades.excluir,
                command: () => this.emitirAcao('excluir')
            },
            {
                label: 'Ir para Reunião',
                icon: 'pi pi-arrow-right',
                styleClass: 'bg-teal-500',
                disabled: !possuiAtividade,
                command: () => this.emitirAcao('ir-para-reuniao')
            },
            {
                label: 'Visualizar Comissão / Núcleo',
                icon: 'pi pi-eye',
                styleClass: 'bg-surface-700',
                disabled: !possuiAtividade,
                command: () => this.emitirAcao('visualizar-comissao-nucleo')
            }
        ];
    }

    /** Menu do botão "Opções": ações gerais + ações da atividade (sempre presentes, habilitadas conforme a seleção). */
    itensOpcoesMenu(): AppMenuItem[] {
        return [...this.itensOpcoes(), ...this.itensAcoesTabela()];
    }

    obterIconeStatus(status: string): string {
        switch (status) {
            case 'Resolvido':
                return 'pi pi-check-circle';
            case 'Pendente':
                return 'pi pi-clock';
            default:
                return 'pi pi-info-circle';
        }
    }

    private emitirAcao(acao: AcaoTabelaAtividadesComissoes): void {
        this.acaoTabela.emit(acao);
    }

    statusSeveridade(status: AtividadeComissao['status']): 'warn' | 'success' {
        return status === 'resolvido' ? 'success' : 'warn';
    }

    statusRotulo(status: AtividadeComissao['status']): string {
        return status === 'resolvido' ? 'Resolvido' : 'Pendente';
    }

    totalRegistrosLabel(): string {
        const total = this.totalRegistros() ?? this.atividades().length;
        const sufixo = total === 1 ? 'registro' : 'registros';
        return `Total de ${sufixo}: ${total}`;
    }
}
